/**
 * Centralized Mixpanel / Firebase Analytics event name constants.
 *
 * All event names in the application MUST come from this file — never use
 * plain string literals for event names in any component or service.
 *
 * Naming convention: `{role}_{surface}_{action}` in snake_case.
 * Every event also auto-carries super properties (role, user_id, school_id,
 * alforaa_id) registered at identify time — call sites only pass
 * event-specific context.
 */

export const EVENTS = {
	// ─── Public / pre-auth ───────────────────────────────────────────────────
	public: {
		LANDING_PAGE_VIEWED: 'landing_page_viewed',
		LANDING_ROLE_CARD_CLICKED: 'landing_role_card_clicked',
		LANDING_LANGUAGE_TOGGLE_CLICKED: 'landing_language_toggle_clicked',
		LANDING_CTA_CLICKED: 'landing_cta_clicked',
	},

	// ─── Student ─────────────────────────────────────────────────────────────
	student: {
		// Auth
		LOGIN_PAGE_VIEWED: 'student_login_page_viewed',
		LOGIN_CLASS_CODE_SUBMITTED: 'student_login_class_code_submitted',
		LOGIN_CLASS_CODE_FAILED: 'student_login_class_code_failed',
		LOGIN_IDENTITY_VERIFIED: 'student_login_identity_verified',
		LOGIN_IDENTITY_FAILED: 'student_login_identity_failed',
		LOGIN_PIN_SUBMITTED: 'student_login_pin_submitted',
		LOGIN_PIN_FAILED: 'student_login_pin_failed',
		LOGIN_FACE_ID_ATTEMPTED: 'student_login_face_id_attempted',
		LOGIN_FACE_ID_SUCCEEDED: 'student_login_face_id_succeeded',
		LOGIN_FACE_ID_FAILED: 'student_login_face_id_failed',
		LOGIN_PIN_SET_COMPLETED: 'student_login_pin_set_completed',
		LOGIN_SUCCEEDED: 'student_login_succeeded',
		LOGOUT_COMPLETED: 'student_logout_completed',

		// Dashboard & nav
		DASHBOARD_VIEWED: 'student_dashboard_viewed',
		DASHBOARD_CURRICULUM_CARD_CLICKED: 'student_dashboard_curriculum_card_clicked',
		DASHBOARD_RECENT_SESSION_CLICKED: 'student_dashboard_recent_session_clicked',
		DASHBOARD_STATS_CARD_CLICKED: 'student_dashboard_stats_card_clicked',
		NAV_DASHBOARD_CLICKED: 'student_nav_dashboard_clicked',
		NAV_CURRICULA_CLICKED: 'student_nav_curricula_clicked',
		NAV_ANALYTICS_CLICKED: 'student_nav_analytics_clicked',
		NAV_PROFILE_CLICKED: 'student_nav_profile_clicked',
		NAV_LOGOUT_CLICKED: 'student_nav_logout_clicked',

		// Curricula
		CURRICULA_LIST_VIEWED: 'student_curricula_list_viewed',
		CURRICULA_SEARCH_SUBMITTED: 'student_curricula_search_submitted',
		CURRICULA_FILTER_CHANGED: 'student_curricula_filter_changed',
		CURRICULA_CURRICULUM_CARD_CLICKED: 'student_curricula_curriculum_card_clicked',
		CURRICULUM_DETAIL_VIEWED: 'student_curriculum_detail_viewed',
		CURRICULUM_MODULE_EXPANDED: 'student_curriculum_module_expanded',
		CURRICULUM_SESSION_ROW_CLICKED: 'student_curriculum_session_row_clicked',
		CURRICULUM_START_SESSION_CLICKED: 'student_curriculum_start_session_clicked',

		// Session
		SESSION_PAGE_VIEWED: 'student_session_page_viewed',
		SESSION_STARTED: 'student_session_started',
		SESSION_VIDEO_PLAYED: 'student_session_video_played',
		SESSION_VIDEO_PAUSED: 'student_session_video_paused',
		SESSION_VIDEO_COMPLETED: 'student_session_video_completed',
		SESSION_SLIDE_ADVANCED: 'student_session_slide_advanced',
		SESSION_QUIZ_QUESTION_VIEWED: 'student_session_quiz_question_viewed',
		SESSION_QUIZ_ANSWER_SUBMITTED: 'student_session_quiz_answer_submitted',
		SESSION_QUIZ_RETRY_CLICKED: 'student_session_quiz_retry_clicked',
		SESSION_DRAWING_SUBMITTED: 'student_session_drawing_submitted',
		SESSION_IMAGE_ANNOTATION_SUBMITTED: 'student_session_image_annotation_submitted',
		SESSION_MEDIA_CAPTURE_OPENED: 'student_session_media_capture_opened',
		SESSION_MEDIA_CAPTURE_SUBMITTED: 'student_session_media_capture_submitted',
		SESSION_CHALLENGE_STARTED: 'student_session_challenge_started',
		SESSION_CHALLENGE_SUBMITTED: 'student_session_challenge_submitted',
		SESSION_FEEDBACK_SUBMITTED: 'student_session_feedback_submitted',
		SESSION_STEP_COMPLETED: 'student_session_step_completed',
		SESSION_STEP_SKIPPED: 'student_session_step_skipped',
		SESSION_EXITED_EARLY: 'student_session_exited_early',
		SESSION_COMPLETED: 'student_session_completed',

		// Profile & analytics
		PROFILE_PAGE_VIEWED: 'student_profile_page_viewed',
		PROFILE_AVATAR_CLICKED: 'student_profile_avatar_clicked',
		PROFILE_EDIT_CLICKED: 'student_profile_edit_clicked',
		PROFILE_PIN_CHANGE_CLICKED: 'student_profile_pin_change_clicked',
		ANALYTICS_PAGE_VIEWED: 'student_analytics_page_viewed',
		ANALYTICS_FILTER_CHANGED: 'student_analytics_filter_changed',
	},

	// ─── Trainer ─────────────────────────────────────────────────────────────
	trainer: {
		// Auth
		LOGIN_PAGE_VIEWED: 'trainer_login_page_viewed',
		LOGIN_SUBMITTED: 'trainer_login_submitted',
		LOGIN_SUCCEEDED: 'trainer_login_succeeded',
		LOGIN_FAILED: 'trainer_login_failed',
		LOGOUT_COMPLETED: 'trainer_logout_completed',

		// Dashboard & nav
		DASHBOARD_VIEWED: 'trainer_dashboard_viewed',
		DASHBOARD_CLASS_CARD_CLICKED: 'trainer_dashboard_class_card_clicked',
		DASHBOARD_STUDENT_ROW_CLICKED: 'trainer_dashboard_student_row_clicked',
		DASHBOARD_STATS_CARD_CLICKED: 'trainer_dashboard_stats_card_clicked',
		NAV_DASHBOARD_CLICKED: 'trainer_nav_dashboard_clicked',
		NAV_CURRICULA_CLICKED: 'trainer_nav_curricula_clicked',
		NAV_CLASSES_CLICKED: 'trainer_nav_classes_clicked',
		NAV_STUDENTS_CLICKED: 'trainer_nav_students_clicked',
		NAV_ANALYTICS_CLICKED: 'trainer_nav_analytics_clicked',
		NAV_LOGOUT_CLICKED: 'trainer_nav_logout_clicked',

		// Classes & students
		CLASS_DETAIL_VIEWED: 'trainer_class_detail_viewed',
		CLASS_STUDENT_ROW_CLICKED: 'trainer_class_student_row_clicked',
		CLASS_ADD_STUDENT_CLICKED: 'trainer_class_add_student_clicked',
		CLASS_STUDENT_ADDED: 'trainer_class_student_added',
		CLASS_STUDENT_REMOVED: 'trainer_class_student_removed',
		STUDENT_DETAIL_VIEWED: 'trainer_student_detail_viewed',
		STUDENT_PIN_RESET_CLICKED: 'trainer_student_pin_reset_clicked',
		STUDENT_PIN_RESET_CONFIRMED: 'trainer_student_pin_reset_confirmed',
		STUDENT_ANALYTICS_VIEWED: 'trainer_student_analytics_viewed',
		STUDENT_SESSION_REVIEW_OPENED: 'trainer_student_session_review_opened',

		// Curricula & session preview
		CURRICULA_LIST_VIEWED: 'trainer_curricula_list_viewed',
		CURRICULA_CURRICULUM_CARD_CLICKED: 'trainer_curricula_curriculum_card_clicked',
		CURRICULUM_DETAIL_VIEWED: 'trainer_curriculum_detail_viewed',
		CURRICULUM_MODULE_EXPANDED: 'trainer_curriculum_module_expanded',
		SESSION_PREVIEW_OPENED: 'trainer_session_preview_opened',
		SESSION_PREVIEW_STEP_VIEWED: 'trainer_session_preview_step_viewed',
		SESSION_PREVIEW_CLOSED: 'trainer_session_preview_closed',
	},

	// ─── Parent ──────────────────────────────────────────────────────────────
	parent: {
		// Auth
		LOGIN_PAGE_VIEWED: 'parent_login_page_viewed',
		LOGIN_MAGIC_LINK_REQUESTED: 'parent_login_magic_link_requested',
		LOGIN_MAGIC_LINK_VERIFIED: 'parent_login_magic_link_verified',
		LOGIN_PIN_SUBMITTED: 'parent_login_pin_submitted',
		LOGIN_PIN_FAILED: 'parent_login_pin_failed',
		LOGIN_PIN_SET_COMPLETED: 'parent_login_pin_set_completed',
		LOGIN_SUCCEEDED: 'parent_login_succeeded',
		LOGOUT_COMPLETED: 'parent_logout_completed',

		// Dashboard & nav
		DASHBOARD_VIEWED: 'parent_dashboard_viewed',
		DASHBOARD_CHILD_SELECTED: 'parent_dashboard_child_selected',
		DASHBOARD_CURRICULUM_CARD_CLICKED: 'parent_dashboard_curriculum_card_clicked',
		DASHBOARD_SESSION_REVIEW_OPENED: 'parent_dashboard_session_review_opened',
		NAV_DASHBOARD_CLICKED: 'parent_nav_dashboard_clicked',
		NAV_ANALYTICS_CLICKED: 'parent_nav_analytics_clicked',
		NAV_LOGOUT_CLICKED: 'parent_nav_logout_clicked',
		ANALYTICS_PAGE_VIEWED: 'parent_analytics_page_viewed',
		ANALYTICS_FILTER_CHANGED: 'parent_analytics_filter_changed',
	},

	// ─── School admin ────────────────────────────────────────────────────────
	schoolAdmin: {
		// Auth & setup
		LOGIN_PAGE_VIEWED: 'school_admin_login_page_viewed',
		LOGIN_SUBMITTED: 'school_admin_login_submitted',
		LOGIN_SUCCEEDED: 'school_admin_login_succeeded',
		LOGIN_FAILED: 'school_admin_login_failed',
		LOGOUT_COMPLETED: 'school_admin_logout_completed',
		SETUP_PAGE_VIEWED: 'school_admin_setup_page_viewed',
		SETUP_PASSWORD_SUBMITTED: 'school_admin_setup_password_submitted',
		SETUP_COMPLETED: 'school_admin_setup_completed',

		// Dashboard & nav
		DASHBOARD_VIEWED: 'school_admin_dashboard_viewed',
		DASHBOARD_STATS_CARD_CLICKED: 'school_admin_dashboard_stats_card_clicked',
		NAV_DASHBOARD_CLICKED: 'school_admin_nav_dashboard_clicked',
		NAV_CLASSES_CLICKED: 'school_admin_nav_classes_clicked',
		NAV_TRAINERS_CLICKED: 'school_admin_nav_trainers_clicked',
		NAV_STUDENTS_CLICKED: 'school_admin_nav_students_clicked',
		NAV_ANALYTICS_CLICKED: 'school_admin_nav_analytics_clicked',
		NAV_LOGOUT_CLICKED: 'school_admin_nav_logout_clicked',

		// Classes
		CLASSES_PAGE_VIEWED: 'school_admin_classes_page_viewed',
		CLASSES_CREATE_CLICKED: 'school_admin_classes_create_clicked',
		CLASSES_CREATE_SUBMITTED: 'school_admin_classes_create_submitted',
		CLASSES_CREATED: 'school_admin_classes_created',
		CLASSES_ROW_CLICKED: 'school_admin_classes_row_clicked',
		CLASSES_EDIT_CLICKED: 'school_admin_classes_edit_clicked',
		CLASSES_UPDATED: 'school_admin_classes_updated',
		CLASSES_DELETE_CLICKED: 'school_admin_classes_delete_clicked',
		CLASSES_DELETED: 'school_admin_classes_deleted',

		// Trainers
		TRAINERS_PAGE_VIEWED: 'school_admin_trainers_page_viewed',
		TRAINERS_CREATE_CLICKED: 'school_admin_trainers_create_clicked',
		TRAINERS_CREATE_SUBMITTED: 'school_admin_trainers_create_submitted',
		TRAINERS_CREATED: 'school_admin_trainers_created',
		TRAINERS_ROW_CLICKED: 'school_admin_trainers_row_clicked',
		TRAINERS_CLASS_ASSIGNED: 'school_admin_trainers_class_assigned',
		TRAINERS_DELETED: 'school_admin_trainers_deleted',

		// Students
		STUDENTS_PAGE_VIEWED: 'school_admin_students_page_viewed',
		STUDENTS_ROSTER_UPLOAD_OPENED: 'school_admin_students_roster_upload_opened',
		STUDENTS_ROSTER_UPLOAD_SUBMITTED: 'school_admin_students_roster_upload_submitted',
		STUDENTS_ROSTER_UPLOAD_COMPLETED: 'school_admin_students_roster_upload_completed',
		STUDENTS_ROW_CLICKED: 'school_admin_students_row_clicked',
		STUDENTS_CLASS_ASSIGNED: 'school_admin_students_class_assigned',
		STUDENTS_DELETED: 'school_admin_students_deleted',

		// Analytics
		ANALYTICS_PAGE_VIEWED: 'school_admin_analytics_page_viewed',
		ANALYTICS_FILTER_CHANGED: 'school_admin_analytics_filter_changed',
	},

	// ─── Master admin ────────────────────────────────────────────────────────
	masterAdmin: {
		// Auth
		LOGIN_PAGE_VIEWED: 'master_admin_login_page_viewed',
		LOGIN_CREDENTIALS_SUBMITTED: 'master_admin_login_credentials_submitted',
		LOGIN_CREDENTIALS_SUCCEEDED: 'master_admin_login_credentials_succeeded',
		LOGIN_CREDENTIALS_FAILED: 'master_admin_login_credentials_failed',
		LOGIN_TOTP_SUBMITTED: 'master_admin_login_totp_submitted',
		LOGIN_TOTP_SUCCEEDED: 'master_admin_login_totp_succeeded',
		LOGIN_TOTP_FAILED: 'master_admin_login_totp_failed',
		LOGOUT_COMPLETED: 'master_admin_logout_completed',

		// Dashboard & nav
		DASHBOARD_VIEWED: 'master_admin_dashboard_viewed',
		DASHBOARD_STATS_CARD_CLICKED: 'master_admin_dashboard_stats_card_clicked',
		NAV_DASHBOARD_CLICKED: 'master_admin_nav_dashboard_clicked',
		NAV_SCHOOLS_CLICKED: 'master_admin_nav_schools_clicked',
		NAV_USERS_CLICKED: 'master_admin_nav_users_clicked',
		NAV_CURRICULUM_CLICKED: 'master_admin_nav_curriculum_clicked',
		NAV_ANALYTICS_CLICKED: 'master_admin_nav_analytics_clicked',
		NAV_AUDIT_LOGS_CLICKED: 'master_admin_nav_audit_logs_clicked',
		NAV_CONFIG_CLICKED: 'master_admin_nav_config_clicked',
		NAV_LOGOUT_CLICKED: 'master_admin_nav_logout_clicked',

		// Schools
		SCHOOLS_PAGE_VIEWED: 'master_admin_schools_page_viewed',
		SCHOOLS_SEARCH_SUBMITTED: 'master_admin_schools_search_submitted',
		SCHOOLS_FILTER_CHANGED: 'master_admin_schools_filter_changed',
		SCHOOLS_CREATE_CLICKED: 'master_admin_schools_create_clicked',
		SCHOOLS_CREATE_SUBMITTED: 'master_admin_schools_create_submitted',
		SCHOOLS_CREATED: 'master_admin_schools_created',
		SCHOOLS_ROW_CLICKED: 'master_admin_schools_row_clicked',
		SCHOOLS_EDIT_CLICKED: 'master_admin_schools_edit_clicked',
		SCHOOLS_UPDATED: 'master_admin_schools_updated',
		SCHOOLS_STATUS_TOGGLED: 'master_admin_schools_status_toggled',
		SCHOOLS_DELETE_CLICKED: 'master_admin_schools_delete_clicked',
		SCHOOLS_DELETED: 'master_admin_schools_deleted',
		SCHOOL_DETAIL_VIEWED: 'master_admin_school_detail_viewed',

		// Users
		USERS_PAGE_VIEWED: 'master_admin_users_page_viewed',
		USERS_FILTER_CHANGED: 'master_admin_users_filter_changed',
		USERS_CREATE_CLICKED: 'master_admin_users_create_clicked',
		USERS_CREATE_SUBMITTED: 'master_admin_users_create_submitted',
		USERS_CREATED: 'master_admin_users_created',
		USERS_ROW_CLICKED: 'master_admin_users_row_clicked',
		USERS_STATUS_TOGGLED: 'master_admin_users_status_toggled',
		USERS_CREDENTIALS_RESET: 'master_admin_users_credentials_reset',
		USERS_DELETED: 'master_admin_users_deleted',

		// Curriculum
		CURRICULUM_LIST_VIEWED: 'master_admin_curriculum_list_viewed',
		CURRICULUM_CREATE_CLICKED: 'master_admin_curriculum_create_clicked',
		CURRICULUM_CREATED: 'master_admin_curriculum_created',
		CURRICULUM_CARD_CLICKED: 'master_admin_curriculum_card_clicked',
		CURRICULUM_DETAIL_VIEWED: 'master_admin_curriculum_detail_viewed',
		CURRICULUM_UPDATED: 'master_admin_curriculum_updated',
		CURRICULUM_DELETED: 'master_admin_curriculum_deleted',
		CURRICULUM_MODULE_EXPANDED: 'master_admin_curriculum_module_expanded',
		CURRICULUM_SESSION_CLICKED: 'master_admin_curriculum_session_clicked',

		// Analytics, audit, config
		ANALYTICS_PAGE_VIEWED: 'master_admin_analytics_page_viewed',
		ANALYTICS_FILTER_CHANGED: 'master_admin_analytics_filter_changed',
		AUDIT_LOGS_PAGE_VIEWED: 'master_admin_audit_logs_page_viewed',
		AUDIT_LOGS_FILTER_CHANGED: 'master_admin_audit_logs_filter_changed',
		AUDIT_LOGS_ROW_CLICKED: 'master_admin_audit_logs_row_clicked',
		CONFIG_PAGE_VIEWED: 'master_admin_config_page_viewed',
		CONFIG_FLAG_TOGGLED: 'master_admin_config_flag_toggled',
		CONFIG_SAVED: 'master_admin_config_saved',
	},
} as const;

/** Union type of all possible event name string values */
type EventValues<T> = T extends Record<string, infer V>
	? V extends string
		? V
		: V extends Record<string, unknown>
			? EventValues<V>
			: never
	: never;
export type EventName = EventValues<typeof EVENTS>;
